import { Card, Text, Button, DropZone, Spinner, Banner, InlineStack, Box, Modal, Icon, Badge, ButtonGroup } from '@shopify/polaris';
import { DeleteIcon, ViewIcon } from '@shopify/polaris-icons';
import { useState, useCallback } from 'react';
import { CardHeading } from './CardHeading';

/**
 * S3DocumentsCard - Shows all S3 documents and allows upload/download/delete
 * @param {string} packageName - Name for the zip download (without .zip extension)
 */
export default function S3DocumentsCard({ resourceId, s3ObjectKeys, onS3KeysChange, apiBasePath = '/api/invoices_in', disabled = false, packageName }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState(null);

  const rawKeys = s3ObjectKeys ? (typeof s3ObjectKeys === 'string' ? JSON.parse(s3ObjectKeys) : s3ObjectKeys) : [];
  // Sort keys: PDFs first, then rest alphabetically
  const keys = [...rawKeys].sort((a, b) => {
    const aIsPdf = a.toLowerCase().endsWith('.pdf');
    const bIsPdf = b.toLowerCase().endsWith('.pdf');
    if (aIsPdf && !bIsPdf) return -1;
    if (!aIsPdf && bIsPdf) return 1;
    return a.localeCompare(b);
  });

  const getFileName = (key) => key.split('/').pop();
  const getFileExtension = (fileName) => fileName.split('.').pop().toLowerCase();

  const getFileIcon = (fileName) => {
    const icons = { pdf: '📄', xml: '📋', json: '📊', png: '🖼️', jpg: '🖼️', jpeg: '🖼️' };
    return icons[getFileExtension(fileName)] || '📁';
  };

  const getFileBadge = (fileName) => {
    const ext = getFileExtension(fileName);
    const tones = { pdf: 'info', xml: 'warning', json: 'success' };
    return <Badge tone={tones[ext]}>{ext.toUpperCase()}</Badge>;
  };

  const handleUpload = useCallback(
    async (droppedFiles, acceptedFiles, rejectedFiles) => {
      console.log('DropZone onDrop called:', { droppedFiles, acceptedFiles, rejectedFiles, resourceId });

      // Polaris DropZone passes (droppedFiles, acceptedFiles, rejectedFiles)
      // droppedFiles contains the actual File objects
      const files = droppedFiles || [];

      if (!files || files.length === 0) {
        setError('No files selected.');
        return;
      }
      if (!resourceId || resourceId === 'new') {
        setError('Please save the document first before uploading files.');
        return;
      }
      setUploading(true);
      setError(null);
      try {
        for (const file of files) {
          console.log('Uploading file:', file.name, 'resourceId:', resourceId);
          const formData = new FormData();
          formData.append('file', file);
          formData.append('invoiceId', String(resourceId));

          // Debug: log FormData contents
          for (const pair of formData.entries()) {
            console.log('FormData:', pair[0], pair[1]);
          }

          const response = await fetch(`${apiBasePath}/upload-s3`, { method: 'POST', body: formData });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Upload response error:', errorData);
            throw new Error(errorData.error || 'Upload failed');
          }
          const result = await response.json();
          if (onS3KeysChange) onS3KeysChange(JSON.stringify(result.allKeys));
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.message || 'Failed to upload file.');
      } finally {
        setUploading(false);
      }
    },
    [resourceId, apiBasePath, onS3KeysChange]
  );

  const handleDelete = useCallback(
    async (s3Key) => {
      if (!confirm(`Delete "${getFileName(s3Key)}"?`)) return;
      setDeleting(s3Key);
      setError(null);
      try {
        const response = await fetch(`${apiBasePath}/delete-s3`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoiceId: resourceId, s3Key })
        });
        if (!response.ok) throw new Error('Delete failed');
        const result = await response.json();
        if (onS3KeysChange) onS3KeysChange(result.allKeys.length > 0 ? JSON.stringify(result.allKeys) : null);
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete file.');
      } finally {
        setDeleting(null);
      }
    },
    [resourceId, apiBasePath, onS3KeysChange]
  );

  const handlePreview = useCallback(
    async (s3Key) => {
      const fileName = getFileName(s3Key);
      const ext = getFileExtension(fileName);
      setPreviewFileName(fileName);
      setPreviewLoading(true);
      setPreviewOpen(true);
      try {
        const response = await fetch(`${apiBasePath}/download?fileKey=${encodeURIComponent(s3Key)}`);
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        if (ext === 'json' || ext === 'xml') {
          setPreviewUrl({ type: 'text', content: await blob.text(), ext });
        } else {
          setPreviewUrl({ type: 'blob', url: URL.createObjectURL(blob), ext });
        }
      } catch (err) {
        console.error('Preview error:', err);
        setError('Failed to load preview.');
        setPreviewOpen(false);
      } finally {
        setPreviewLoading(false);
      }
    },
    [apiBasePath]
  );

  const handleDownload = useCallback(
    async (s3Key) => {
      const fileName = getFileName(s3Key);
      try {
        const response = await fetch(`${apiBasePath}/download?fileKey=${encodeURIComponent(s3Key)}`);
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Download error:', err);
        setError('Failed to download file.');
      }
    },
    [apiBasePath]
  );

  const handleDownloadPackage = useCallback(async () => {
    if (keys.length === 0) return;
    setDownloading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBasePath}/download-package?invoiceId=${resourceId}`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${packageName || `attachments_${resourceId}`}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Package download error:', err);
      setError('Failed to download package.');
    } finally {
      setDownloading(false);
    }
  }, [apiBasePath, resourceId, keys.length, packageName]);

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl?.type === 'blob') URL.revokeObjectURL(previewUrl.url);
    setPreviewUrl(null);
  };

  const renderPreviewContent = () => {
    if (previewLoading)
      return (
        <Box padding="400">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      );
    if (!previewUrl) return null;
    if (previewUrl.type === 'text') {
      return (
        <div style={{ height: '85vh', overflow: 'auto', background: 'var(--p-color-bg-surface-secondary)', padding: '12px', borderRadius: '8px' }}>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '11px', margin: 0 }}>{previewUrl.content}</pre>
        </div>
      );
    }
    if (previewUrl.ext === 'pdf') return <iframe title="PDF Preview" src={previewUrl.url} style={{ width: '100%', height: '85vh', border: 'none' }} />;
    if (['png', 'jpg', 'jpeg'].includes(previewUrl.ext))
      return (
        <Box padding="300">
          <img src={previewUrl.url} alt={previewFileName} style={{ maxWidth: '100%', maxHeight: '85vh' }} />
        </Box>
      );
    return <Text>Preview not available for this file type.</Text>;
  };

  return (
    <Card>
      <CardHeading
        title={`Attachments${keys.length > 0 ? ` (${keys.length})` : ''}`}
        actions={keys.length > 0 ? [{ content: downloading ? 'Downloading...' : '📦 Download package', onAction: handleDownloadPackage, disabled: downloading }] : null}
      />

      {error && (
        <Box paddingBlockEnd="200">
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        </Box>
      )}

      {keys.length > 0 && (
        <Box paddingBlockEnd="200">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {keys.map((key) => {
              const fileName = getFileName(key);
              const isDeleting = deleting === key;
              return (
                <div
                  key={key}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', background: 'var(--p-color-bg-surface-secondary)', borderRadius: '6px' }}
                >
                  <InlineStack gap="200" blockAlign="center">
                    <Text variant="bodySm">{getFileIcon(fileName)}</Text>
                    <Text variant="bodySm" fontWeight="medium">
                      {fileName}
                    </Text>
                    {getFileBadge(fileName)}
                  </InlineStack>
                  <ButtonGroup>
                    <Button size="micro" icon={<Icon source={ViewIcon} />} onClick={() => handlePreview(key)} disabled={disabled || isDeleting} accessibilityLabel="Preview" />
                    <Button size="micro" onClick={() => handleDownload(key)} disabled={disabled || isDeleting} accessibilityLabel="Download">
                      ⬇
                    </Button>
                    <Button size="micro" tone="critical" icon={<Icon source={DeleteIcon} />} onClick={() => handleDelete(key)} disabled={disabled} loading={isDeleting} accessibilityLabel="Delete" />
                  </ButtonGroup>
                </div>
              );
            })}
          </div>
        </Box>
      )}

      <DropZone onDrop={handleUpload} disabled={disabled || uploading || resourceId === 'new'} variableHeight>
        {uploading ? (
          <Box padding="300">
            <InlineStack align="center" gap="200">
              <Spinner size="small" />
              <Text variant="bodySm">Uploading...</Text>
            </InlineStack>
          </Box>
        ) : (
          <Box padding="300">
            <Text variant="bodySm" tone="subdued" alignment="center">
              Drop files here or click to upload
            </Text>
          </Box>
        )}
      </DropZone>

      <Modal open={previewOpen} onClose={closePreview} title={previewFileName} size="large">
        <Modal.Section flush>{renderPreviewContent()}</Modal.Section>
      </Modal>
    </Card>
  );
}
